'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface Reference {
  name: string;
  phone: string;
  relationship: string;
}

export interface DriverApplication {
  id?: string;
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  dateOfBirth: string;
  // Driver's License Information
  licenseNumber: string;
  licenseClass: string;
  licenseExpiryDate: string;
  licenseProvince: string;
  yearsLicensed: number;
  // Vehicle Information
  vehicleType: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  licensePlate: string;
  insuranceProvider: string;
  insurancePolicyNumber: string;
  insuranceExpiryDate: string;
  // Background Check Consent
  backgroundCheckConsent: boolean;
  criminalRecordCheckConsent: boolean;
  drivingRecordCheckConsent: boolean;
  // Availability Schedule
  availabilitySchedule: Record<string, string[]>;
  preferredHoursPerWeek?: number;
  // Driving Experience
  yearsDrivingExperience: number;
  commercialDrivingExperience: boolean;
  movingDeliveryExperience: boolean;
  experienceDescription?: string;
  // Screening Questions
  whyJoin: string;
  customerServiceExperience?: string;
  physicalCapabilityConfirmation: boolean;
  smartphoneAccess: boolean;
  // References
  references: Reference[];
  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  // Application Status
  status?: string;
  adminNotes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  // Terms
  termsAccepted: boolean;
  privacyPolicyAccepted: boolean;
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export const driverApplicationService = {
  async submitApplication(application: DriverApplication) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('driver_applications')
        .insert({
          first_name: application.firstName,
          last_name: application.lastName,
          email: application.email,
          phone: application.phone,
          address: application.address,
          city: application.city,
          province: application.province,
          postal_code: application.postalCode,
          date_of_birth: application.dateOfBirth,
          license_number: application.licenseNumber,
          license_class: application.licenseClass,
          license_expiry_date: application.licenseExpiryDate,
          license_province: application.licenseProvince,
          years_licensed: application.yearsLicensed,
          vehicle_type: application.vehicleType,
          vehicle_make: application.vehicleMake,
          vehicle_model: application.vehicleModel,
          vehicle_year: application.vehicleYear,
          license_plate: application.licensePlate,
          insurance_provider: application.insuranceProvider,
          insurance_policy_number: application.insurancePolicyNumber,
          insurance_expiry_date: application.insuranceExpiryDate,
          background_check_consent: application.backgroundCheckConsent,
          criminal_record_check_consent: application.criminalRecordCheckConsent,
          driving_record_check_consent: application.drivingRecordCheckConsent,
          availability_schedule: application.availabilitySchedule,
          preferred_hours_per_week: application.preferredHoursPerWeek,
          years_driving_experience: application.yearsDrivingExperience,
          commercial_driving_experience: application.commercialDrivingExperience,
          moving_delivery_experience: application.movingDeliveryExperience,
          experience_description: application.experienceDescription,
          why_join: application.whyJoin,
          customer_service_experience: application.customerServiceExperience,
          physical_capability_confirmation: application.physicalCapabilityConfirmation,
          smartphone_access: application.smartphoneAccess,
          references: application.references,
          emergency_contact_name: application.emergencyContactName,
          emergency_contact_phone: application.emergencyContactPhone,
          emergency_contact_relationship: application.emergencyContactRelationship,
          terms_accepted: application.termsAccepted,
          privacy_policy_accepted: application.privacyPolicyAccepted,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Submit application error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        status: data.status,
        createdAt: data.created_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAllApplications() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('driver_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Get applications error:', error.message);
          return [];
        }
      }

      return data?.map((row) => ({
        id: row.id,
        firstName: row.first_name,
        lastName: row.last_name,
        email: row.email,
        phone: row.phone,
        address: row.address,
        city: row.city,
        province: row.province,
        postalCode: row.postal_code,
        dateOfBirth: row.date_of_birth,
        licenseNumber: row.license_number,
        licenseClass: row.license_class,
        licenseExpiryDate: row.license_expiry_date,
        licenseProvince: row.license_province,
        yearsLicensed: row.years_licensed,
        vehicleType: row.vehicle_type,
        vehicleMake: row.vehicle_make,
        vehicleModel: row.vehicle_model,
        vehicleYear: row.vehicle_year,
        licensePlate: row.license_plate,
        insuranceProvider: row.insurance_provider,
        insurancePolicyNumber: row.insurance_policy_number,
        insuranceExpiryDate: row.insurance_expiry_date,
        backgroundCheckConsent: row.background_check_consent,
        criminalRecordCheckConsent: row.criminal_record_check_consent,
        drivingRecordCheckConsent: row.driving_record_check_consent,
        availabilitySchedule: row.availability_schedule,
        preferredHoursPerWeek: row.preferred_hours_per_week,
        yearsDrivingExperience: row.years_driving_experience,
        commercialDrivingExperience: row.commercial_driving_experience,
        movingDeliveryExperience: row.moving_delivery_experience,
        experienceDescription: row.experience_description,
        whyJoin: row.why_join,
        customerServiceExperience: row.customer_service_experience,
        physicalCapabilityConfirmation: row.physical_capability_confirmation,
        smartphoneAccess: row.smartphone_access,
        references: row.references,
        emergencyContactName: row.emergency_contact_name,
        emergencyContactPhone: row.emergency_contact_phone,
        emergencyContactRelationship: row.emergency_contact_relationship,
        status: row.status,
        adminNotes: row.admin_notes,
        reviewedBy: row.reviewed_by,
        reviewedAt: row.reviewed_at,
        termsAccepted: row.terms_accepted,
        privacyPolicyAccepted: row.privacy_policy_accepted,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateApplication(id: string, updates: { status?: string; adminNotes?: string; reviewedBy?: string }) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (updates.status) updateData.status = updates.status;
      if (updates.adminNotes !== undefined) updateData.admin_notes = updates.adminNotes;
      if (updates.reviewedBy) {
        updateData.reviewed_by = updates.reviewedBy;
        updateData.reviewed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('driver_applications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update application error:', error.message);
          return null;
        }
      }

      return data;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async deleteApplication(id: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('driver_applications')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Delete application error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkUpdateStatus(ids: string[], status: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('driver_applications')
        .update({ status })
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk update error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkDelete(ids: string[]) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('driver_applications')
        .delete()
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk delete error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};